<template>
  <div>
    <div class="p-booking__filters">
      <div class="p-booking__title">{{ dictionary.chooseMovie }}</div>
      <div class="row">
        <BookingFilters
          v-if="desktop"
          v-model="selectedFilters"
          :filters-data="allScreenFiltersData"
          @filter-changed="filterChanged" />
      </div>
    </div>

    <Swiper
      v-if="filteredRecords.length"
      :key="keySwiper"
      ref="swiperMovie"
      :navigation="{
        nextEl: '.s-playing-now__next',
        prevEl: '.s-playing-now__prev',
      }"
      :pagination="{
        el: '.s-playing-now__pagination',
        type: 'bullets',
        clickable: true,
      }"
      :breakpoints="{
        320: {
          slidesPerView: 1.5,
          spaceBetween: 8,
        },
        768: {
          slidesPerView: 3.5,
          spaceBetween: 32,
        },
        // 992: {
        //   slidesPerView: 5,
        //   spaceBetween: 32,
        // },
        1280: {
          slidesPerView: 6,
          spaceBetween: 32,
        },
      }"
      :initial-slide="getStartSlide()"
      @swiper="onMovieSwiper">
      <SwiperSlide
        v-for="record in filteredRecords"
        :id="record.id"
        :key="record.id"
        :class="unselectedMovieEffect(record.movieId)">
        <MovieCard
          :is-booking-flow="true"
          :title="record.title"
          :subtitle="record.subTitle"
          :description="record.desc"
          :image="record.thumb"
          :pg="String(record.pg)"
          :duration="record.dur"
          :dolby="record.dolby"
          :three-dimensions="record.threeDimensions"
          :imax="record.imax"
          :imax3D="record.imax3D"
          :sphera="false"
          :movie-url="record.url"
          :dictionary="dictionary"
          :transl="record.transl"
          :video-id="record.vid"
          :play="false"
          :only-members="record.onlyMembers"
          :elements-show-options="{
            showBookingCta: false,
            showDuration: false,
            showGenres: false,
            showDesc: false,
            posterCardDimensions: {},
          }"
          :selected="isMovieSelected(record)"
          @click="chooseMovie(record)" />
      </SwiperSlide>

      <div class="swiper__nav--center mt-5">
        <div
          v-if="desktop"
          class="swiper-button-prev s-playing-now__prev"></div>
        <div class="swiper-pagination s-playing-now__pagination"></div>
        <div
          v-if="desktop"
          class="swiper-button-next s-playing-now__next"></div>
      </div>
    </Swiper>
    <NoResults v-else />
  </div>
</template>

<script setup>
import jquery from 'jquery'
import BookingFilters from './../../BookingFilters.vue'
import { v4 as uuidv4 } from 'uuid'
import {
  getCurrentInstance,
  nextTick,
  onBeforeMount,
  onMounted,
  ref,
  toRaw,
} from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import MovieCard from '../../MovieCard.vue'
import { useBookingStore } from '../../../store/BookingStore'
import { useRoute } from 'vue-router'
import useViewport from '../../../../features/viewport'
import NoResults from './../../../components/Shared/NoResults.vue'
import * as lib from './../../../../lib/lib'

const offsetY = lib.scrDesktops() ? 50 : 75
const emit = defineEmits(['choose-movie'])

const { dictionary } = getCurrentInstance().appContext.config.globalProperties
const { desktop } = useViewport()
const route = useRoute()
const routeMovieId = route.query?.movieId
const routeScreen = route.query?.screen
const store = useBookingStore()
const allFilters = store.allFilters
const allScreenFiltersData = ref({
  cinemas: [],
  screenTypes: [],
  roomTypes: [],
})

const swiperRef = ref(null)
const filteredRecords = ref([])
const movies = ref([])
const selectedFilters = ref({
  cinemas: 'All',
  screenTypes: 'All',
  roomTypes: 'All',
})
const filtersData = ref({})
const keySwiper = ref(uuidv4())

const allScreens = lib.getData('bookingData').screens

onBeforeMount(() => {
  assignMoviesData()

  allScreenFiltersData.value = {
    cinemas: toRaw(allFilters.cinemas),
    screenTypes: toRaw(allFilters.screenTypes),
    roomTypes: toRaw(allFilters.roomTypes),
  }
})

onMounted(() => {
  chooseMovieByRoute()
})

const getStartSlide = () => {
  return routeMovieId
    ? filteredRecords.value.findIndex((slide) => slide.id === routeMovieId)
    : 0
}

const chooseMovieByRoute = () => {
  if (routeMovieId) {
    const entry = movies.value.find((i) => i.movieId === routeMovieId)
    if (entry) {
      chooseMovie(entry)
    }
  }
}

const assignMoviesData = () => {
  movies.value = [...store.movies]
  filteredRecords.value = [...store.movies]
  filtersData.value = store.allFilters
}

const filterChanged = (filters) => {
  store.setSelectedFilters(filters)
  keySwiper.value = uuidv4()
  store.resetAll()

  if (Object.values(filters).every((filter) => filter === 'All')) {
    filteredRecords.value = movies.value
  } else {
    filteredRecords.value = filterMoviesData(filters)
  }
}
const filterMoviesData = (filters) => {
  const filteredRecords = allScreens.filter((record) => {
    const cinemaMatch =
      filters.cinemas === 'All' || record.cinemaId === filters.cinemas

    const roomTypeMatch =
      filters.roomTypes === 'All' ||
      record.roomTypes.includes(filters.roomTypes)

    const screenTypeMatch =
      filters.screenTypes === 'All' || record.screenType === filters.screenTypes

    return cinemaMatch && roomTypeMatch && screenTypeMatch
  })

  return movies.value.filter((record) => {
    return filteredRecords.some((i) => i.scheduledFilmId === record.movieId)
  })
}

const isMovieSelected = (movie) => {
  return store.bookingProgress.selectedMovie?.movieId === movie.movieId
}

const chooseMovie = async (movie) => {
  store.selectMovie(movie)
  emit('choose-movie')

  // do not scroll is screen is selected
  // app navs user straight to step2
  // there is no need for scrolling for this case
  if (!routeScreen) {
    await nextTick()
    lib.scrollToElem(jquery('#cinemaStep'), offsetY)
  }
}

const unselectedMovieEffect = (movieId) => {
  const { selectedMovie } = store.bookingProgress

  return selectedMovie && movieId !== selectedMovie.movieId
    ? 'p-booking__unselected'
    : ''
}

const onMovieSwiper = (swiper) => {
  swiperRef.value = swiper
}
</script>
